/**
 * Sport-aware rate display for Analytics charts and sidebars.
 * Running/walking use pace (sec/km or sec/mi); cycling and others use speed.
 */

import { METERS_PER_MILE, speedValue } from "./format";
import { canonicalSportForFilter } from "./sportNormalization";

export const ANALYTICS_RATE_MODE = {
  SPEED: "speed",
  PACE: "pace",
};

const PACE_SPORTS = new Set(["running", "walking"]);

export function classifyAnalyticsRateMode(sport = "") {
  const canonical = canonicalSportForFilter(
    sport && typeof sport === "object" ? sport : { sport }
  );
  if (PACE_SPORTS.has(canonical)) return ANALYTICS_RATE_MODE.PACE;
  return ANALYTICS_RATE_MODE.SPEED;
}

export function usesPaceAnalytics(sport) {
  return classifyAnalyticsRateMode(sport) === ANALYTICS_RATE_MODE.PACE;
}

export function analyticsRateUnit(sport = "", unitSystem = "metric") {
  if (usesPaceAnalytics(sport)) {
    return unitSystem === "imperial" ? "/mi" : "/km";
  }
  return unitSystem === "imperial" ? "mph" : "km/h";
}

export function analyticsRateLabel(sport = "", isSegmentMode = false) {
  const base = usesPaceAnalytics(sport) ? "Pace" : "Speed";
  return isSegmentMode ? `${base} · Seg` : base;
}

export function getRateChartDataKey(sport) {
  return usesPaceAnalytics(sport) ? "pace_sec" : "speed_kmh";
}

/** Chart speed values are km/h (metric) or mph (imperial) from speedValue(). */
export function speedDisplayToPaceSeconds(speedDisplay, unitSystem = "metric") {
  if (speedDisplay == null || !Number.isFinite(speedDisplay) || speedDisplay <= 0) return null;
  return 3600 / speedDisplay;
}

export function speedMsToPaceSeconds(ms, unitSystem = "metric") {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
  const metersPerUnit = unitSystem === "imperial" ? METERS_PER_MILE : 1000;
  return metersPerUnit / ms;
}

export function formatPaceSeconds(paceSec) {
  if (paceSec == null || !Number.isFinite(paceSec) || paceSec <= 0) return null;
  const total = Math.round(paceSec);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function formatRateSidebarValueFromMs(ms, sport, unitSystem = "metric") {
  if (ms == null || !Number.isFinite(ms)) return null;
  if (usesPaceAnalytics(sport)) {
    return formatPaceSeconds(speedMsToPaceSeconds(ms, unitSystem));
  }
  return Number(speedValue(ms, unitSystem).toFixed(1));
}

export function formatRateSidebarValueFromChartSpeed(speedDisplay, sport, unitSystem = "metric") {
  if (speedDisplay == null || !Number.isFinite(speedDisplay)) return null;
  if (usesPaceAnalytics(sport)) {
    return formatPaceSeconds(speedDisplayToPaceSeconds(speedDisplay, unitSystem));
  }
  return Number(Number(speedDisplay).toFixed(1));
}

export function formatRateLiveDisplay(value, sport) {
  if (value == null || Number.isNaN(value)) return null;
  if (usesPaceAnalytics(sport)) {
    return formatPaceSeconds(value);
  }
  return Number(value).toFixed(1);
}

export function enrichChartDataWithRate(data, sport, unitSystem = "metric") {
  if (!usesPaceAnalytics(sport)) return data;
  return data.map((row) => ({
    ...row,
    pace_sec:
      row.speed_kmh != null && Number.isFinite(row.speed_kmh) && row.speed_kmh > 0
        ? speedDisplayToPaceSeconds(row.speed_kmh, unitSystem)
        : null,
  }));
}

/** Faster pace (lower seconds) should appear higher on the chart. */
export function paceYAxisDomain(data, dataKey = "pace_sec") {
  const values = data
    .map((row) => row[dataKey])
    .filter((value) => value != null && Number.isFinite(value) && value > 0);
  if (!values.length) return ["auto", "auto"];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max(15, (max - min) * 0.08);
  return [max + padding, Math.max(0, min - padding)];
}

export function paceChartStats(data, dataKey = "pace_sec") {
  const values = data
    .map((row) => row[dataKey])
    .filter((value) => value != null && Number.isFinite(value) && value > 0);
  if (!values.length) return { max: null, avg: null, maxNumeric: null, avgNumeric: null };
  const fastest = Math.min(...values);
  const avgNumeric = values.reduce((sum, value) => sum + value, 0) / values.length;
  return {
    max: formatPaceSeconds(fastest),
    avg: formatPaceSeconds(avgNumeric),
    maxNumeric: fastest,
    avgNumeric,
  };
}

export function speedChartStats(data, dataKey = "speed_kmh", decimals = 1) {
  const values = data
    .map((row) => row[dataKey])
    .filter((value) => value != null && !Number.isNaN(value));
  if (!values.length) return { max: null, avg: null, maxNumeric: null, avgNumeric: null };
  const maxNumeric = Math.max(...values);
  const avgNumeric = values.reduce((sum, value) => sum + value, 0) / values.length;
  const fmt = (value) => (decimals ? Number(value.toFixed(decimals)) : Math.round(value));
  return {
    max: fmt(maxNumeric),
    avg: fmt(avgNumeric),
    maxNumeric,
    avgNumeric,
  };
}

export function streamRateChartStats(data, sport, unitSystem = "metric", dataKey = "speed_kmh") {
  if (usesPaceAnalytics(sport)) {
    const withPace = enrichChartDataWithRate(data, sport, unitSystem);
    return paceChartStats(withPace, "pace_sec");
  }
  return speedChartStats(data, dataKey, 1);
}
